import React, {
  useCallback,
  useEffect,
  useRef,
  useState
} from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';

import {
  Base,
  ContextMenu,
  ContextMenuItem,
  IconButton
} from '@brandwatch/axiom-components';

import SlidePickerFilterInput from './SlidePickerFilterInput';
import SlidePickerMenuItem from './SlidePickerMenuItem';

import './SlidePickerSheet.css';

const SlidePickerSheet = ({
  children,
  slideIndex,
  filteredSlides = [],
  filterValue = '',
  onItemClick,
  onFilterChange,
  onOpen,
  onClose,
  setScrollLock
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [isFilterFocussed, setIsFilterFocussed] = useState(false);

  const filterInputNode = useRef(null);
  const bodyCoverNode = useRef(null);

  const onFilterInputNode = useCallback((node) => {
    filterInputNode.current = node;
  }, []);

  const onBodyNode = useCallback((node) => {
    bodyCoverNode.current = node;
  }, []);

  const onBodyCoverTouch = useCallback((e) => {
    if (e.cancelable) {
      e.preventDefault();
    }

    setIsFilterFocussed(false);
  }, []);

  const onClick = useCallback(() => setIsOpen(true), []);

  const onFilterFocus = useCallback(() => setIsFilterFocussed(true), []);

  const onFilterBlur = useCallback(() => setIsFilterFocussed(false), []);

  const onItemClickLocal = useCallback((index) => {
    setIsOpen(false);
    onItemClick(index);
  }, [onItemClick]);

  const onCloseLocal = useCallback(() => {
    setIsOpen(false);
  }, [onClose]);

  useEffect(() => {
    setScrollLock(isOpen);

    if (isOpen) {
      onOpen();
    } else {
      onClose();
    }
  }, [isOpen]);

  useEffect(() => {
    if (filterInputNode.current) {
      if (!isFilterFocussed) {
        filterInputNode.current.blur();
      }
    }
  }, [isFilterFocussed]);

  // Prevent scrolling from happening whilst the OS keyboard is up
  useEffect(() => {
    if (bodyCoverNode.current) {
      if (isFilterFocussed) {
        bodyCoverNode.current.addEventListener("touchmove", onBodyCoverTouch, {passive: false});
      } else {
        bodyCoverNode.current.removeEventListener("touchmove", onBodyCoverTouch, {passive: false});
      }
    }
  }, [isFilterFocussed, onBodyCoverTouch]);

  return (
    <React.Fragment>
      {children({onClick})}
      <div className={classNames('SlidePickerSheet', {
        'SlidePickerSheet--isOpen': isOpen
      })}>
        <Base
          theme="day"
          textColor="body"
          className="SlidePickerSheet__layout"
        >
          <div className="SlidePickerSheet__header">
            <div className="SlidePickerSheet__filter">
              <div className="SlidePickerSheet__filter__input">
                <SlidePickerFilterInput
                  value={filterValue}
                  onBlur={onFilterBlur}
                  onFocus={onFilterFocus}
                  onChange={onFilterChange}
                  onInputNode={onFilterInputNode}
                />
              </div>
            </div>
            <div className="SlidePickerSheet__close">
              <IconButton size="small" name="cross" onClick={onCloseLocal} />
            </div>
          </div>
          <div className="SlidePickerSheet__body" ref={onBodyNode}>
            <ContextMenu maxHeight="100%">
              {filteredSlides.map((slide) => {
                return (
                  <SlidePickerMenuItem
                    key={slide.index}
                    Component={ContextMenuItem}
                    index={slide.index}
                    label={slide.name}
                    isActive={slideIndex === slide.index}
                    onClick={onItemClickLocal}
                  />
                );
              })}
            </ContextMenu>
          </div>
        </Base>
      </div>
    </React.Fragment>
  )
};

SlidePickerSheet.propTypes = {
  children: PropTypes.func.isRequired,
  slideIndex: PropTypes.number,
  filteredSlides: PropTypes.array,
  filterValue: PropTypes.string,
  onItemClick: PropTypes.func.isRequired,
  onOpen: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
  onFilterChange: PropTypes.func.isRequired
};

export default SlidePickerSheet;
